import React from "react";
import { fromJS } from "immutable";
import { SelectOption, Switch, Text } from "@showitinc/showit-wc";

import Input from "../../../base/Input";
import AppConstants from "../../../../constants/AppConstants";
import GalleryConstants from "../../../../constants/GalleryConstants";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import {
  GALLERY_AUTO_ADVANCE_TIME,
  GALLERY_DOTS,
  GALLERY_DOTS_COLOR,
  GALLERY_DOTS_GAP,
  GALLERY_DOTS_LOCATION,
  GALLERY_DOTS_OFFSET,
  GALLERY_DOTS_SELECTED_COLOR,
  GALLERY_DOTS_SIZE,
  GALLERY_IMAGE_PADDING,
  GALLERY_INACTIVE_IMAGES,
  GALLERY_INACTIVE_IMAGES_BLUR,
  GALLERY_INACTIVE_IMAGES_OPACITY,
  GALLERY_INACTIVE_IMAGES_PRELOAD,
  GALLERY_INACTIVE_IMAGES_SCALE,
  GALLERY_NO_LOOP,
  SLIDING_GALLERY_CONFIG,
  SLIDING_GALLERY_TYPE,
} from "../../../../constants/MergeConstants";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { initFormatter } from "../../../../utils/PropertyFormatters";
import { COLOR_PICKER, SELECT } from "../../../../constants/InputConstants.mjs";
import { prepareInputUtils } from "../../../../utils/InputUtils";
import { MIXED } from "../../../../constants/DesignConstants.mjs";
import { isValueMixed } from "../../../../utils/InheritanceUtils";
import LeftOffset from "../../../../components/base/LeftOffset";

const { UPDATE_ACTION_DELETE, UPDATE_ACTION_SET } = AppConstants;
const {
  SLIDING_DOT_ACTIVE_COLOR,
  SLIDING_DOT_BASE_COLOR,
  SLIDING_DOT_GAP,
  SLIDING_DOT_LOC,
  SLIDING_DOT_LOC_IN,
  SLIDING_DOT_LOC_OUT,
  SLIDING_DOT_OFFSET,
  SLIDING_DOT_SIZE,
  SLIDING_PRELOAD,
  SLIDING_TYPE_FILMSTRIP,
  SLIDING_TYPE_SINGLE_FILL,
  SLIDING_TYPE_SINGLE_FIT,
} = GalleryConstants;

const defaults = fromJS({
  gallery: {
    aa: 0,
    dots: false,
    imgInactive: false,
    imgPad: 0,
    noloop: 0,
    preload: SLIDING_PRELOAD,
  },
});

const defaultDotProps = fromJS({
  gap: SLIDING_DOT_GAP,
  offset: SLIDING_DOT_OFFSET,
  size: SLIDING_DOT_SIZE,
});

const defaultInactiveProps = fromJS({
  o: 100,
  blur: 0,
  scale: 1,
});

const initDotProps = {
  ac: SLIDING_DOT_ACTIVE_COLOR,
  c: SLIDING_DOT_BASE_COLOR,
  loc: SLIDING_DOT_LOC,
};

const initInactiveImageProps = {
  o: 100,
  blur: 0,
  scale: 100,
};

const GalleryDots = ({ disabled, element, layout, onPropUpdate, preparedElement }) => {
  const _onDotsChange = (propInfo) => {
    const { value } = propInfo;
    const newPropInfo = {
      ...propInfo,
      action: value === false ? UPDATE_ACTION_DELETE : UPDATE_ACTION_SET,
      value: {
        ...initDotProps,
      },
    };

    onPropUpdate(newPropInfo);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout
  );

  const elemProps = defaults.mergeDeep(gatherProps(preparedElement, layout, [GALLERY_DOTS], true));
  const { canMerge, prepareValue, isSyncMixed, isMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  const synced = isSynced(GALLERY_DOTS);
  const initialDotProps = elemProps.getIn(GALLERY_DOTS.split("."));
  const enabled = !isMixed(GALLERY_DOTS) ? initialDotProps !== false : false;
  const controls = [];

  if (enabled) {
    const dotProps = defaultDotProps.merge(initialDotProps);

    const separatePath = (path) => path.split(".").slice(-1)[0];
    const getValue = (path) => dotProps.get(separatePath(path));
    const prepChange = (path) => [path, getValue(path)];
    const getChildPropName = (propName) => getPropName(propName, synced);

    const { onChange } = initFormatter(onPropUpdate, getChildPropName);

    controls.push(
      <LeftOffset key="gallery-dots-controls">
        <Input
          color={prepareValue(GALLERY_DOTS_COLOR)}
          disabled={disabled}
          inputType={COLOR_PICKER}
          key="dot-base-color"
          label="Default Color"
          onSIBeforeChange={onChange(...prepChange(GALLERY_DOTS_COLOR))}
          onSIChange={onChange(...prepChange(GALLERY_DOTS_COLOR))}
          onSIInput={onChange(...prepChange(GALLERY_DOTS_COLOR), false)}
        />
        <Input
          color={prepareValue(GALLERY_DOTS_SELECTED_COLOR)}
          disabled={disabled}
          inputType={COLOR_PICKER}
          key="dot-active-color"
          label="Active Color"
          onSIBeforeChange={onChange(...prepChange(GALLERY_DOTS_SELECTED_COLOR))}
          onSIChange={onChange(...prepChange(GALLERY_DOTS_SELECTED_COLOR))}
          onSIInput={onChange(...prepChange(GALLERY_DOTS_SELECTED_COLOR), false)}
        />
        <Input
          disabled={disabled}
          key="dot-size"
          label="Size"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_DOTS_SIZE))}
          onSIInput={onChange(...prepChange(GALLERY_DOTS_SIZE), false)}
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(GALLERY_DOTS_SIZE) ?? 10}
        />
        <Input
          disabled={disabled}
          key="dot-gap"
          label="Gap"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_DOTS_GAP))}
          onSIInput={onChange(...prepChange(GALLERY_DOTS_GAP), false)}
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(GALLERY_DOTS_GAP) ?? 10}
        />
        <Input
          disabled={disabled}
          key="dot-offset"
          label="Image Offset"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_DOTS_OFFSET))}
          onSIInput={onChange(...prepChange(GALLERY_DOTS_OFFSET), false)}
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(GALLERY_DOTS_OFFSET) ?? 10}
        />
        <Input
          disabled={disabled}
          key="dot-loc"
          inputType={SELECT}
          label="Location"
          shift="left"
          onSISelect={onChange(...prepChange(GALLERY_DOTS_LOCATION))}
          value={prepareValue(GALLERY_DOTS_LOCATION)}
        >
          <SelectOption value={SLIDING_DOT_LOC_OUT}>Under Images</SelectOption>
          <SelectOption value={SLIDING_DOT_LOC_IN}>Over Images</SelectOption>
        </Input>
      </LeftOffset>
    );
  }

  const { onChange: onSwitch } = initFormatter(_onDotsChange, getPropName);

  return (
    <>
      <Text className="panel-subheading">Image Dots</Text>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_DOTS)}
        sync={synced}
        toggleSync={() => toggleSyncedProp(GALLERY_DOTS)}
      >
        <Switch
          context={canMerge(GALLERY_DOTS)}
          checked={enabled}
          disabled={disabled}
          label="Show Dots"
          onSIChange={onSwitch(GALLERY_DOTS, initialDotProps)}
        />
        {controls}
      </SplitMergeWrapper>
    </>
  );
};

const GalleryInactiveImages = ({ disabled, element, layout, onPropUpdate, preparedElement }) => {
  const _onCustomInactiveImageChange = (propInfo) => {
    const { value } = propInfo;
    const nwePropInfo = {
      ...propInfo,
      action: value === true ? UPDATE_ACTION_SET : UPDATE_ACTION_DELETE,
      value: {
        ...initInactiveImageProps,
      },
    };

    onPropUpdate(nwePropInfo);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout
  );

  const elemProps = defaults.mergeDeep(
    gatherProps(preparedElement, layout, [GALLERY_INACTIVE_IMAGES], true)
  );

  const { canMerge, prepareValue, isSyncMixed, isMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  const synced = isSynced(GALLERY_INACTIVE_IMAGES);
  const initialInactiveProps = elemProps.getIn(GALLERY_INACTIVE_IMAGES.split("."));
  const enabled = !isMixed(GALLERY_INACTIVE_IMAGES) ? initialInactiveProps !== false : false;
  const controls = [];

  if (enabled) {
    const inactiveProps = defaultInactiveProps.merge(initialInactiveProps);
    const preload = prepareValue(GALLERY_INACTIVE_IMAGES_PRELOAD);

    const separatePath = (path) => path.split(".").slice(-1)[0];
    const getValue = (path) => inactiveProps.get(separatePath(path));
    const prepChange = (path) => [path, getValue(path)];
    const getChildPropName = (propName) => getPropName(propName, synced);

    const { onChange } = initFormatter(onPropUpdate, getChildPropName);

    controls.push(
      <LeftOffset key="gallery-inactive-controls">
        <Input
          disabled={disabled}
          key="inactive-o"
          min="0"
          max="100"
          step="10"
          label="Opacity"
          onSIBeforeChange={onChange(...prepChange(GALLERY_INACTIVE_IMAGES_OPACITY))}
          onSIInput={onChange(...prepChange(GALLERY_INACTIVE_IMAGES_OPACITY), false)}
          type={INPUT_TYPE_NUMBER}
          units="%"
          value={prepareValue(GALLERY_INACTIVE_IMAGES_OPACITY)}
        />
        <Input
          disabled={disabled}
          key="inactive-blur"
          label="Blur"
          max="20"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_INACTIVE_IMAGES_BLUR))}
          onSIInput={onChange(...prepChange(GALLERY_INACTIVE_IMAGES_BLUR), false)}
          step="10"
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(GALLERY_INACTIVE_IMAGES_BLUR)}
        />
        <Input
          disabled={disabled}
          key="inactive-scale"
          label="Scale"
          max="100"
          min="25"
          onSIBeforeChange={onChange(...prepChange(GALLERY_INACTIVE_IMAGES_SCALE))}
          onSIInput={onChange(...prepChange(GALLERY_INACTIVE_IMAGES_SCALE), false)}
          step="5"
          type={INPUT_TYPE_NUMBER}
          units="%"
          value={prepareValue(GALLERY_INACTIVE_IMAGES_SCALE)}
        />
        <Input
          disabled={disabled}
          key="gal-preload"
          label="Preloaded"
          max="50"
          min="3"
          onSIBeforeChange={onChange(GALLERY_INACTIVE_IMAGES_PRELOAD, preload)}
          onSIInput={onChange(GALLERY_INACTIVE_IMAGES_PRELOAD, preload, false)}
          step="1"
          type={INPUT_TYPE_NUMBER}
          value={preload}
        />
      </LeftOffset>
    );
  }

  const { onChange: onSwitch } = initFormatter(_onCustomInactiveImageChange, getPropName);

  return (
    <>
      <Text className="panel-subheading">Unselected Image</Text>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_INACTIVE_IMAGES)}
        sync={synced}
        toggleSync={() => toggleSyncedProp(GALLERY_INACTIVE_IMAGES)}
      >
        <Switch
          context={canMerge(GALLERY_INACTIVE_IMAGES)}
          checked={enabled}
          label="Customize"
          disabled={disabled}
          onSIChange={onSwitch(GALLERY_INACTIVE_IMAGES)}
        />
        {controls}
      </SplitMergeWrapper>
    </>
  );
};

const GallerySlidingProperties = ({ disabled, element, layout, onPropUpdate, preparedElement }) => {
  const _onLoopChange = (propInfo) => {
    const { value } = propInfo;
    const newPropUpdate = {
      ...propInfo,
      action: value === true ? UPDATE_ACTION_DELETE : UPDATE_ACTION_SET,
      value: 1,
    };

    onPropUpdate(newPropUpdate);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    element,
    onPropUpdate,
    layout
  );

  const elemProps = defaults.mergeDeep(
    gatherProps(
      preparedElement,
      layout,
      [GALLERY_AUTO_ADVANCE_TIME, GALLERY_IMAGE_PADDING, GALLERY_NO_LOOP, SLIDING_GALLERY_TYPE],
      true
    )
  );

  const separatePath = (path) => path.split(".");
  const getValue = (path) => elemProps.getIn(separatePath(path));
  const prepChange = (path) => [path, getValue(path)];

  const { onChange: onStyleChange } = initFormatter(onPropUpdate);
  const { onChange } = initFormatter(onPropUpdate, getPropName);
  const { onChange: onLoopChange } = initFormatter(_onLoopChange, getPropName);

  const { canMerge, prepareValue, isSyncMixed, isMixed } = prepareInputUtils(
    layout,
    preparedElement,
    elemProps
  );

  const { prepareValue: prepareTopLevelValue, isMixed: isTopLevelValueMixed } = prepareInputUtils(
    undefined,
    preparedElement
  );

  const noLoop =
    !isSyncMixed(GALLERY_NO_LOOP) && !isMixed(GALLERY_NO_LOOP)
      ? getValue(GALLERY_NO_LOOP) !== 1
      : false;

  const style = isTopLevelValueMixed(SLIDING_GALLERY_CONFIG)
    ? MIXED
    : prepareTopLevelValue(SLIDING_GALLERY_TYPE) ?? SLIDING_TYPE_FILMSTRIP;

  return (
    <>
      <Input
        disabled={disabled}
        inputType={SELECT}
        label="Style"
        onSISelect={onStyleChange(...prepChange(SLIDING_GALLERY_TYPE))}
        reserveContext
        shift="left"
        value={style}
      >
        <SelectOption value={SLIDING_TYPE_FILMSTRIP}>Carousel</SelectOption>
        <SelectOption value={SLIDING_TYPE_SINGLE_FILL}>Single Image - Fill</SelectOption>
        <SelectOption value={SLIDING_TYPE_SINGLE_FIT}>Single Image - Fit</SelectOption>
      </Input>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_IMAGE_PADDING)}
        sync={isSynced(GALLERY_IMAGE_PADDING)}
        toggleSync={() => toggleSyncedProp(GALLERY_IMAGE_PADDING)}
      >
        <Input
          context={canMerge(GALLERY_IMAGE_PADDING)}
          disabled={disabled}
          label="Gap"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_IMAGE_PADDING))}
          onSIInput={onChange(...prepChange(GALLERY_IMAGE_PADDING), false)}
          step="2"
          type={INPUT_TYPE_NUMBER}
          units="px"
          value={prepareValue(GALLERY_IMAGE_PADDING)}
        />
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_AUTO_ADVANCE_TIME)}
        sync={isSynced(GALLERY_AUTO_ADVANCE_TIME)}
        toggleSync={() => toggleSyncedProp(GALLERY_AUTO_ADVANCE_TIME)}
      >
        <Input
          context={canMerge(GALLERY_AUTO_ADVANCE_TIME)}
          disabled={disabled}
          label="Auto Advance"
          min="0"
          onSIBeforeChange={onChange(...prepChange(GALLERY_AUTO_ADVANCE_TIME))}
          onSIInput={onChange(...prepChange(GALLERY_AUTO_ADVANCE_TIME), false)}
          step="0.25"
          type={INPUT_TYPE_NUMBER}
          units="sec"
          value={prepareValue(GALLERY_AUTO_ADVANCE_TIME)}
        />
      </SplitMergeWrapper>
      <SplitMergeWrapper
        layout={layout}
        mixed={isSyncMixed(GALLERY_NO_LOOP)}
        sync={isSynced(GALLERY_NO_LOOP)}
        toggleSync={() => toggleSyncedProp(GALLERY_NO_LOOP)}
      >
        <Switch
          context={canMerge(GALLERY_NO_LOOP)}
          checked={noLoop}
          disabled={disabled}
          label="Loop Images"
          onSIChange={onLoopChange(GALLERY_NO_LOOP, noLoop)}
        />
      </SplitMergeWrapper>
      <GalleryDots
        disabled={disabled}
        element={element}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={preparedElement}
      />
      <GalleryInactiveImages
        disabled={disabled}
        element={element}
        layout={layout}
        onPropUpdate={onPropUpdate}
        preparedElement={preparedElement}
      />
    </>
  );
};

export default GallerySlidingProperties;
